import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import type { User } from 'firebase/auth';
import { isTestMode, mockSub, onTestSubUpdate } from '../testMode';
import type { Promotion } from '../api/promotions';

// Stripe is the source of truth.
// 'active' = paid:true (whether in Stripe trial or fully paying)
// 'expired' = had a subscription but cancelled
// 'none' = never paid
export type SubscriptionStatus = 'active' | 'expired' | 'none';

export interface SubscriptionInfo {
  status: SubscriptionStatus | null;
  loading: boolean;
  plan: 'monthly' | 'yearly' | 'code' | null;
  paidVia: 'stripe' | 'code' | null;
  // True if the subscription is in its Stripe-managed trial period
  inTrial: boolean;
  trialEnd: Date | null;
  trialDaysLeft: number | null;
  // For paid subscriptions: when the next billing happens
  currentPeriodEnd: Date | null;
  // For code-redeemed users
  codeExpiresAt: Date | null;
  codeDaysLeft: number | null;
  // True if the user has gone through Stripe checkout (has a customer ID)
  hasStripeCustomer: boolean;
  // True if the subscription is set to cancel at period end
  cancelAtPeriodEnd: boolean;
  // True if user has ever started a trial. Once true, never reset.
  // Used to prevent re-using the free trial offer.
  hasUsedTrial: boolean;
  // True for a trusted member who may edit/archive library techniques
  // (set by the admin via setJanitor; shown in Settings and gates the pencil).
  janitor: boolean;
  // Their own Spotlight Promotion, at whatever stage it has reached. Read from
  // the user doc rather than stats/promotions, because a pending or rejected
  // one is never published and only its owner ever sees it.
  promotion: Promotion | null;
  // Access frozen while a Promotion runs — a prepaid year or gift-code days,
  // handed back if they cancel. Non-null means cancelling ends the
  // subscription outright rather than dropping to the membership price.
  promoHeldDays: number | null;
}

const empty: SubscriptionInfo = {
  status: null,
  loading: false,
  plan: null,
  paidVia: null,
  inTrial: false,
  trialEnd: null,
  trialDaysLeft: null,
  currentPeriodEnd: null,
  codeExpiresAt: null,
  codeDaysLeft: null,
  hasStripeCustomer: false,
  cancelAtPeriodEnd: false,
  hasUsedTrial: false,
  janitor: false,
  promotion: null,
  promoHeldDays: null,
};

export function useSubscription(user: User | null): SubscriptionInfo {
  const [info, setInfo] = useState<SubscriptionInfo>(
    isTestMode && mockSub ? mockSub : { ...empty, loading: !isTestMode }
  );

  useEffect(() => {
    if (isTestMode) {
      return onTestSubUpdate((patch) => setInfo((prev) => ({ ...prev, ...patch })));
    }
    if (!user) {
      // Defer to avoid set-state-in-effect lint
      Promise.resolve().then(() => setInfo(empty));
      return;
    }

    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        if (!snap.exists()) {
          setInfo({ ...empty, status: 'none' });
          return;
        }

        const data = snap.data();
        const paid = data.paid === true;
        const hasStripeCustomer = !!data.stripeCustomerId;
        const paidVia = (data.paidVia as 'stripe' | 'code') || (data.redeemedCode ? 'code' : (paid ? 'stripe' : null));
        const plan = (data.plan as 'monthly' | 'yearly' | 'code') || (paidVia === 'code' ? 'code' : null);

        // Trial info from Stripe.
        // Trial is only "active" if trialEnd is in the future. Don't trust subscriptionStatus
        // 'trialing' alone because the webhook may not have fired yet to update it.
        const trialEnd = data.trialEnd?.toDate?.() || null;
        const inTrial = !!(trialEnd && trialEnd.getTime() > Date.now());
        const trialDaysLeft = inTrial && trialEnd
          ? Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
          : null;

        // Billing info
        const currentPeriodEnd = data.currentPeriodEnd?.toDate?.() || null;
        const cancelAtPeriodEnd = !!data.cancelAtPeriodEnd;

        // Code info
        let codeExpiresAt: Date | null = null;
        let codeDaysLeft: number | null = null;
        if (paidVia === 'code') {
          if (data.codeExpiresAt?.toDate) {
            codeExpiresAt = data.codeExpiresAt.toDate();
          } else if (data.paidAt?.toDate) {
            codeExpiresAt = new Date(data.paidAt.toDate().getTime() + 365 * 24 * 60 * 60 * 1000);
          }
          if (codeExpiresAt) {
            codeDaysLeft = Math.max(0, Math.ceil((codeExpiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
          }
        }

        // Edge case: cancelled subscription whose access period has ended.
        // Stripe webhook should mark paid:false but might not have fired yet.
        // If the user is set to cancel at period end AND there's no future access date,
        // treat them as expired so they get the re-subscribe flow.
        const accessEnded = !inTrial && (!currentPeriodEnd || currentPeriodEnd.getTime() < Date.now());
        const effectivelyExpired = paid && cancelAtPeriodEnd && accessEnded;
        // A gift that has run out is over, whatever `paid` still says: nothing
        // rewrites the doc when a code lapses, so the check has to live here
        // (and in igvideo's server-side gate — keep the two in step).
        const codeLapsed = paidVia === 'code' && !!codeExpiresAt && codeExpiresAt.getTime() < Date.now();

        const status: SubscriptionStatus = effectivelyExpired || codeLapsed
          ? 'expired'
          : paid
            ? 'active'
            : (hasStripeCustomer || data.redeemedCode ? 'expired' : 'none');

        setInfo({
          status,
          loading: false,
          plan,
          paidVia,
          inTrial,
          trialEnd,
          trialDaysLeft,
          currentPeriodEnd,
          codeExpiresAt,
          codeDaysLeft,
          hasStripeCustomer,
          cancelAtPeriodEnd,
          hasUsedTrial: data.hasUsedTrial === true,
          janitor: data.janitor === true,
          promotion: (data.promotion as Promotion) ?? null,
          promoHeldDays: (data.promoHeldDays as number) ?? null,
        });
      },
      () => {
        setInfo({ ...empty, status: 'none' });
      }
    );

    return unsub;
  }, [user]);

  return info;
}
